import { AfterViewInit, Component, EventEmitter, inject, OnInit, Output } from '@angular/core'
import { NgClass, NgForOf, NgIf } from '@angular/common'
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { TypeReferenceDto } from '../../../sunleon/data-access/http/dto/type-reference-dto'
import { ReferenceService } from '../../../sunleon/data-access/http/reference.service'
import { ZoneMeasures } from '../../model/zone-measures'
import { GeomapDomainService } from '../../domain/geomap.domain'
import { CalepinageService } from '../../data-access/calepinage.service'
import { CustomValidators } from '../../../shared/validators/custom-validators'
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'

@Component({
  selector: 'app-form-zone2',
  templateUrl: './form-zone2.component.html',
  styleUrls: ['./form-zone2.component.scss'],
  standalone: true,
  imports: [NgIf, FormsModule, NgForOf, ReactiveFormsModule, NgClass],
})
export class FormZone2Component implements OnInit, AfterViewInit {
  @Output()
  formReady: EventEmitter<FormGroup> = new EventEmitter<FormGroup>()

  private readonly geomapDomainService = inject(GeomapDomainService)
  private readonly calepinageService = inject(CalepinageService)
  readonly measuresZone2$ = this.geomapDomainService.getAllMeasuresForZone$(1)

  azimutLabel = "Azimut (compris entre -180 et 180, SUD = 0)"

  zone2Form: FormGroup
  typePoses: TypeReferenceDto[] = []
  typeToitures: TypeReferenceDto[] = []
  typePlaces: TypeReferenceDto[] = []
  typeParkings: TypeReferenceDto[] = []

  onRadioClickZone2(code: string | null, controleName: string) {
    this.zone2Form.get(controleName).setValue(code)
    this.geomapDomainService.selectTypePoseOutils(1)
    this.geomapDomainService.selectionBarreOutils()
    this.geomapDomainService.selectionDessinOutilsSeulementZone()
  }

  ngAfterViewInit(): void {
    this.measuresZone2$.subscribe((zoneMeasures: ZoneMeasures) => {
      this.zone2Form.get('surfaceZone2').patchValue(zoneMeasures.surface === 0 ? null : zoneMeasures.surface)
      this.zone2Form.get('azimutZone2').patchValue(zoneMeasures.azimuth === 0 ? null : zoneMeasures.azimuth)
      this.zone2Form.get('polygonsZone2').patchValue(zoneMeasures.polygons);
      this.zone2Form.get('azimutCoordinateZone2').patchValue(zoneMeasures.azimutCoordinates);
      if ((zoneMeasures.polygons?.length ?? 0) > 0 && this.zone2Form.get('puissanceChoisieZone2')?.value != null) {
        this.zone2Form.get('puissanceChoisieZone2')?.patchValue(null, { emitEvent: false })
      }
      this.updateZoneValueValidators()
      this.zone2Form.updateValueAndValidity({ emitEvent: false })
    })

    this.calepinageService.registerZoneAutoTrigger(1, this.zone2Form, {
      typePose: 'typePoseZone2',
      typeToiture: 'typeToitureZone2',
      inclinaison: 'inclinaisonZone2',
      typeVehiculeParking: 'typeVehiculeParkingZone2',
      typePlaceParking: 'typePlaceParkingZone2',
      orientation: 'orientationTerrasseZone2',
    })
  }

  constructor(private formBuilder: FormBuilder, private referenceService: ReferenceService, private modalService: NgbModal) {
    this.zone2Form = this.formBuilder.group({
      typePoseZone2: [null],
      surfaceZone2: [null, [Validators.pattern('^\\d+(\\.\\d{1,2})?$')]],
      azimutZone2: [null, [Validators.pattern('^-?(180|\\d{1,2}|1[0-7]\\d)$')]],
      typeToitureZone2: [''],
      orientationTerrasseZone2: ['SUD'],
      pvReadyZone2: [false],
      inclinaisonZone2: [null, [Validators.pattern('^\\d+$'), this.inclinaisonValidator()]],
      typeVehiculeParkingZone2: [''],
      typePlaceParkingZone2: [''],
      puissanceChoisieZone2: [null, [Validators.pattern('^\\d+(\\.\\d{1,2})?$')]],
      polygonsZone2: [null],
      azimutCoordinateZone2: [null]
    }, { validators: this.zoneSurfaceOrPowerValidator() })

    this.initTypePoseZone(
      'typePoseZone2',
      'deposeCandelabre',
      'poseCandelabre',
      'eclairageSousOmbriere',
      'descenteEpAlu',
      'nbAbattageArbres',
      'mlClotureSol',
      'nbPortailsSol',
      "mlGardeCorpsProvisoire",
      "mlLigneDeVie",
      "nbEchelleCrinoline",
      'surfaceZone2',
      'azimutZone2',
      'typeToitureZone2',
      'pvReadyZone2',
      'inclinaisonZone2',
      'typeVehiculeParkingZone2',
      'typePlaceParkingZone2',
      'puissanceChoisieZone2'
    )

    this.zone2Form.get('puissanceChoisieZone2')?.valueChanges.subscribe(() => {
      this.updateZoneValueValidators()
      this.zone2Form.updateValueAndValidity({ emitEvent: false })
    })
  }

  ngOnInit(): void {
    this.referenceService.getTypePose().subscribe((data) => {
      this.typePoses = data
    })
    this.referenceService.getTypeToiture().subscribe((data) => {
      this.typeToitures = data.filter(t => t.code !== 'BAC_ACIER_SEC')
    })
    this.referenceService.getTypeParking().subscribe((data) => {
      this.typeParkings = data
    })
    this.referenceService.getTypePlace().subscribe((data) => {
      this.typePlaces = data
    })
    this.updateZoneValueValidators()
    this.zone2Form.updateValueAndValidity({ emitEvent: false })
    this.formReady.emit(this.zone2Form)
  }

  initTypePoseZone(
    typePose: string,
    deposeCandelabre: string,
    poseCandelabre: string,
    eclairageSousOmbriere: string,
    descenteEpAlu: string,
    nbAbattageArbres: string,
    mlClotureSol: string,
    nbPortailsSol: string,
    mlGardeCorpsProvisoire: string,
    mlLigneDeVie: string,
    nbEchelleCrinoline: string,
    surfaceZone: string,
    azimutZone: string,
    typeToitureZone: string,
    pvReadyZone: string,
    inclinaisonZone: string,
    typeVehiculeParkingZone: string,
    typePlaceParkingZone: string,
    puissanceChoisieZone: string
  ) {
    this.zone2Form.get(typePose).valueChanges.subscribe((typePoseZone) => {
      const constSurfaceZone = this.zone2Form.get(surfaceZone)
      const constAzimutZone = this.zone2Form.get(azimutZone)
      const constTypeToitureZone = this.zone2Form.get(typeToitureZone)
      const constPvReadyZone = this.zone2Form.get(pvReadyZone)
      const constInclinaisonZone = this.zone2Form.get(inclinaisonZone)
      const constTypeVehiculeParkingZone = this.zone2Form.get(typeVehiculeParkingZone)
      const constTypePlaceParkingZone = this.zone2Form.get(typePlaceParkingZone)

      this.geomapDomainService.currentZone.typeVehiculeParking = '';
      this.geomapDomainService.activateMeasureAreaTool()

      if (typePoseZone) {
        this.updateZoneValueValidators()

        if (typePoseZone === 'TOITURE') {
          constTypeToitureZone.setValidators([CustomValidators.requiredIfControlHasValue(typePose)])
          constPvReadyZone.setValidators([CustomValidators.requiredIfControlHasValue(typePose)])
          constInclinaisonZone.setValidators([
            Validators.required,
            Validators.pattern('^\\d+$'),
            this.inclinaisonValidator()
          ])        
          constTypeToitureZone.reset()
          constPvReadyZone.reset()
          constInclinaisonZone.reset()

          constTypeVehiculeParkingZone.setValidators([Validators.nullValidator])
          constTypePlaceParkingZone.setValidators([Validators.nullValidator])
          constTypeVehiculeParkingZone.reset()
          constTypePlaceParkingZone.reset()
        }
        if (typePoseZone === 'OMBRIERE') {
          constTypeVehiculeParkingZone.setValidators([CustomValidators.requiredIfControlHasValue(typePose)])
          constTypeVehiculeParkingZone.reset()
          constTypePlaceParkingZone.setValidators([Validators.nullValidator])
          constTypePlaceParkingZone.reset()

          constTypeToitureZone.setValidators([Validators.nullValidator])
          constPvReadyZone.setValidators([Validators.nullValidator])
          constInclinaisonZone.setValidators([Validators.nullValidator])

          constTypeToitureZone.reset()
          constPvReadyZone.reset()
          constInclinaisonZone.reset()
        }

        if (typePoseZone === 'SOL') {
          constTypeToitureZone.setValidators([Validators.nullValidator])
          constPvReadyZone.setValidators([Validators.nullValidator])
          constInclinaisonZone.setValidators([Validators.nullValidator])
          constTypeVehiculeParkingZone.setValidators([Validators.nullValidator])
          constTypePlaceParkingZone.setValidators([Validators.nullValidator])

          constTypeToitureZone.reset()
          constPvReadyZone.reset()
          constInclinaisonZone.reset()
          constTypeVehiculeParkingZone.reset()
          constTypePlaceParkingZone.reset()
        }
      } else {
        constSurfaceZone.setValidators([Validators.pattern('^\\d+(\\.\\d{1,2})?$')])
        constAzimutZone.setValidators([Validators.pattern('^-?(180|\\d{1,2}|1[0-7]\\d)$')])
        constTypeToitureZone.setValidators([Validators.nullValidator])
        constPvReadyZone.setValidators([Validators.nullValidator])
        constInclinaisonZone.setValidators([Validators.nullValidator])
        constTypeVehiculeParkingZone.setValidators([Validators.nullValidator])
        constTypePlaceParkingZone.setValidators([Validators.nullValidator])

        constSurfaceZone.reset()
        constAzimutZone.reset()
        constTypeToitureZone.reset()
        constPvReadyZone.reset()
        constInclinaisonZone.reset()
        constTypeVehiculeParkingZone.reset()
        constTypePlaceParkingZone.reset()
      }

      constSurfaceZone.updateValueAndValidity({ emitEvent: false })
      constAzimutZone.updateValueAndValidity({ emitEvent: false })
    })

    this.zone2Form.get(typeVehiculeParkingZone).valueChanges.subscribe((typeVehiculeParking) => {
      const constTypeVehiculeParkingZone = this.zone2Form.get(typeVehiculeParkingZone)
      const constTypePlaceParkingZone = this.zone2Form.get(typePlaceParkingZone)
      constTypeVehiculeParkingZone.setValidators([CustomValidators.requiredIfControlHasValue(typePose)])
      if (typeVehiculeParking === 'VL') {
        constTypePlaceParkingZone.setValidators([CustomValidators.requiredIfControlHasValue(typePose)])
      } else {
        constTypePlaceParkingZone.setValidators([Validators.nullValidator])
        constTypePlaceParkingZone.reset()
      }
    })

    this.zone2Form.get(typeToitureZone).valueChanges.subscribe((typeToiture) => {
      const constAzimutZone = this.zone2Form.get(azimutZone)
      this.zone2Form.get(inclinaisonZone).updateValueAndValidity();
      if (typeToiture === 'BETON' || typeToiture === 'TAN_ETANCHE') {
        this.azimutLabel = "Azimut (compris entre -90 et 90, SUD = 0)"
        this.updateZoneValueValidators()
      } else {
        this.azimutLabel = "Azimut (compris entre -180 et 180, SUD = 0)"
        this.updateZoneValueValidators()
      }
      constAzimutZone.updateValueAndValidity({ emitEvent: false })
    })

    this.zone2Form.get(azimutZone).valueChanges.subscribe((azimut) => {
      const constAzimutZone = this.zone2Form.get(azimutZone)
      const constTypeToitureZone = this.zone2Form.get(typeToitureZone)
      if (constTypeToitureZone.value === 'BETON' || constTypeToitureZone.value === 'TAN_ETANCHE') {
        this.azimutLabel = "Azimut (compris entre -90 et 90, SUD = 0)"
        this.updateZoneValueValidators()
      } else {
        this.azimutLabel = "Azimut (compris entre -180 et 180, SUD = 0)"
        this.updateZoneValueValidators()
      }
      constAzimutZone.updateValueAndValidity({ emitEvent: false })
    })

  }

  onPuissanceChoisieFocus(event: FocusEvent): void {
    if (!this.hasDrawnSurface()) {
      return
    }

    const input = event.target as HTMLInputElement | null
    input?.blur()
    const modalRef = this.modalService.open(ConfirmationModalComponent)
    modalRef.componentInstance.message = `L'ajout d'une puissance dans "Puissance choisie" entrainera la suppression des surfaces dessinées précédemment.`
    modalRef.componentInstance.confirmation = 'Voulez vous continuer ?'
    modalRef.result.then((confirmed: boolean) => {
      if (!confirmed) {
        return
      }
      this.geomapDomainService.clearZoneDrawings(1)
      this.zone2Form.patchValue({
        surfaceZone2: null,
        polygonsZone2: null,
      }, { emitEvent: false })
      this.updateZoneValueValidators()
      setTimeout(() => input?.focus())
    })
  }

  selectVehicule(typeVehicule: string, typePlace: string | null): void {
    const oldTypeVehicule = this.zone2Form.get('typeVehiculeParkingZone2')?.value;
    const oldTypePlace = this.zone2Form.get('typePlaceParkingZone2')?.value;

    const newSelection = `${typeVehicule}_${typePlace}`;
    const oldSelection = `${oldTypeVehicule}_${oldTypePlace}`;

    if (newSelection !== oldSelection && this.geomapDomainService.currentZone.vectorLayer.getSource().getFeatures().length > 0) {
      const modalRef = this.modalService.open(ConfirmationModalComponent);
      modalRef.result.then((confirmed : any) => {
        if (confirmed) {
          this.geomapDomainService.resetDessinModeValorisation();
          this.applySelection(typeVehicule, typePlace);
        }
      });
    } else {
      this.applySelection(typeVehicule, typePlace);
    }
  }

  private applySelection(typeVehicule: string, typePlace: string | null): void {
    this.zone2Form.get('typeVehiculeParkingZone2')?.setValue(typeVehicule);
    this.zone2Form.get('typePlaceParkingZone2')?.setValue(typePlace);
    this.geomapDomainService.currentZone.typeVehiculeParking = this.zone2Form.get('typeVehiculeParkingZone2')?.value + '_' + this.zone2Form.get('typePlaceParkingZone2')?.value;
    this.geomapDomainService.activateMeasureAreaTool()
  }
  
  isVLSelected(place: string): boolean {
    return this.zone2Form.get('typeVehiculeParkingZone2')?.value === 'VL' &&
           this.zone2Form.get('typePlaceParkingZone2')?.value === place;
  }

  inclinaisonValidator() {
  return (control: any) => {
    if (!control || !this.zone2Form) return null;

    const inclinaison = control.value;
    const typeToiture = this.zone2Form.get('typeToitureZone2')?.value;

    if (inclinaison == null || inclinaison === '') return null;

    if (typeToiture === 'BETON' || typeToiture === 'TAN_ETANCHE') {
      if (inclinaison > 3) {
        return { inclinaisonMax: true };
      }
    }

    if (typeToiture === 'BAC_ACIER_SEC_EXISTANT' || typeToiture === 'BAC_ACIER_SEC_NEUF') {
      if (inclinaison <= 3) {
        return { inclinaisonMin: true };
      }
    }

    return null;
  };
}

private updateZoneValueValidators(): void {
  const typePose = this.zone2Form.get('typePoseZone2')?.value
  const puissanceChoisie = this.zone2Form.get('puissanceChoisieZone2')?.value
  const hasPower = puissanceChoisie !== null && puissanceChoisie !== undefined && puissanceChoisie !== ''
  const surfaceControl = this.zone2Form.get('surfaceZone2')
  const azimutControl = this.zone2Form.get('azimutZone2')
  const typeToiture = this.zone2Form.get('typeToitureZone2')?.value

  surfaceControl?.setValidators(hasPower || !typePose ? [Validators.pattern('^\\d+(\\.\\d{1,2})?$')] : [Validators.required, Validators.pattern('^\\d+(\\.\\d{1,2})?$')])

  const azimutPattern =
    typeToiture === 'BETON' || typeToiture === 'TAN_ETANCHE'
      ? '^-?(90|[0-8]?[0-9])$'
      : '^-?(180|\\d{1,3}|1[0-7]\\d)$'

  azimutControl?.setValidators(hasPower || !typePose ? [Validators.pattern(azimutPattern)] : [Validators.required, Validators.pattern(azimutPattern)])
  surfaceControl?.updateValueAndValidity({ emitEvent: false, onlySelf: true })
  azimutControl?.updateValueAndValidity({ emitEvent: false, onlySelf: true })
}

private zoneSurfaceOrPowerValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const typePose = control.get('typePoseZone2')?.value
    if (!typePose) {
      return null
    }
    const surface = control.get('surfaceZone2')?.value
    const puissanceChoisie = control.get('puissanceChoisieZone2')?.value
    const hasSurface = surface !== null && surface !== undefined && surface !== ''
    const hasPower = puissanceChoisie !== null && puissanceChoisie !== undefined && puissanceChoisie !== ''
    return hasSurface || hasPower ? null : { surfaceOrPowerRequired: true }
  }
}

private hasDrawnSurface(): boolean {
  const polygons = this.zone2Form.get('polygonsZone2')?.value as unknown[] | null
  const surface = this.zone2Form.get('surfaceZone2')?.value
  return !!(polygons && polygons.length > 0) || !!surface
}
}
